import { zodResolver } from "@hookform/resolvers/zod"
import { useState, useMemo } from "react"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { gql } from "~/__generated__"
import { ContactTableFragment } from "~/__generated__/graphql"
import { useSafeMutation } from "~/common/use-safe-mutation"
import { TagField, TagFieldTag } from "~/fields/tag-field"
import { Button } from "~/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "~/ui/dialog"
import { GraphqlError } from "~/ui/errors"
import { Form } from "~/ui/form"
import { useToast } from "~/ui/use-toast"

export const CONTACT_UPDATE_MUTATION = gql(/* GraphQL */ `
  mutation UpdateContact($input: ContactUpdateInput!) {
    contactUpdate(input: $input) {
      contact {
        id
        notes
        tags {
          id
          name
        }
      }
    }
  }
`)

const formSchema = z.object({
  tagInput: z.string().optional(),
})

type FormData = z.infer<typeof formSchema>

interface ContactUpdateDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  contact: ContactTableFragment
  onSuccess?: () => void
}

export function ContactUpdateDialog({
  open,
  onOpenChange,
  contact,
  onSuccess,
}: ContactUpdateDialogProps) {
  const { toast } = useToast()
  const [selectedTags, setSelectedTags] = useState<TagFieldTag[]>([])

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      tagInput: "",
    },
  })

  const [updateContact, { loading: updating, error }] = useSafeMutation(CONTACT_UPDATE_MUTATION)

  // Initialize selected tags from contact
  const initialTags = useMemo(() => {
    return contact.tags.map((tag) => ({ ...tag, isNew: false }))
  }, [contact.tags])

  // Reset selected tags when contact changes or dialog opens
  if (open && selectedTags.length === 0 && initialTags.length > 0) {
    setSelectedTags(initialTags)
  }

  const handleSubmit = async (data: FormData) => {
    try {
      const existingTags = selectedTags.filter((tag) => !tag.isNew)
      const newTagsFromSelected = selectedTags.filter((tag) => tag.isNew)
      const tagIds = existingTags.length > 0 ? existingTags.map((tag) => tag.id) : undefined
      const newTags = [
        ...newTagsFromSelected.map((tag) => tag.name),
        ...(data.tagInput?.trim() ? [data.tagInput.trim()] : []),
      ]

      const result = await updateContact({
        variables: {
          input: {
            id: contact.id,
            contactInput: {
              tagIds: tagIds,
              newTags: newTags.length > 0 ? newTags : undefined,
            },
          },
        },
        refetchQueries: ["GetContacts"],
      })

      if (result.data?.contactUpdate?.contact) {
        toast({
          title: "Contact updated",
          description: "Tags have been updated successfully.",
        })

        form.reset()
        setSelectedTags([])
        onOpenChange(false)
        onSuccess?.()
      }
    } catch (err) {
      console.error("Error updating contact:", err)
      toast({
        title: "Error",
        description: "Failed to update contact. Please try again.",
        variant: "destructive",
      })
    }
  }

  const handleClose = () => {
    form.reset()
    setSelectedTags([])
    onOpenChange(false)
  }

  if (error) {
    return <GraphqlError error={error} />
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Update Contact Tags</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            <TagField
              control={form.control}
              name="tagInput"
              label="Tags"
              selectedTags={selectedTags}
              onTagsChange={setSelectedTags}
              disabled={updating}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={updating}>
                {updating ? "Updating..." : "Update Tags"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
